import axios from 'axios';
import { apiService } from './ApiService';
import { PartResult, ExtSearchResults, ClarificationHint, ClarificationChatTurn, ClarificationChatReply, ClarificationChatHistoryMessage } from '../components/features/chat/types';

/** Request body of POST /api/chat/clarification-hint. */
export interface ClarificationHintRequest {
  session_id: string | null;
  question_id: string | null;
  part_name: string;
  part_parameters: string[];
  options: Array<{
    id: string;
    label: string;
    callout: string | null;
    part_number: string | null;
    extra_details: Record<string, string> | null;
    probability_score: number | null;
    /** Diagram area the candidate sits on, so the hint can say which AREA to look in. */
    section_key?: string | null;
    section_label?: string | null;
  }>;
  vehicle: Record<string, any> | null;
  language: string | null;
}

/** Request body of POST /api/chat/clarification-chat. The server answers from
 *  the session's own copy of the pending question when it has it; `options`
 *  is the fallback. */
export interface ClarificationChatRequest extends Omit<ClarificationHintRequest, 'options'> {
  question_text: string | null;
  options: ClarificationHintRequest['options'];
  messages: ClarificationChatTurn[];
}

export interface ChatMessage {
  message: string;
  session_id: string;
}

export interface ChatResponse {
  response: string;
  status: string;
}

export interface UserSession {
  id: string;
  session_id: string;
  user_id: string;
  lastUpdateTime: string;
  updated_at: string;
  app_name: string;
}

export interface UserSessionsResponse {
  status: string;
  sessions: UserSession[];
}

/** A callout hotspot on a section diagram (matches the common ImageWithCallouts shape). */
export interface DiagramCallout {
  label: string;
  bounds: { x: number; y: number; w: number; h: number };
  normalized?: boolean;
}

/** Response of GET /api/chat/section-diagram. `diagram_url` is null when the
 *  catalog has no image; `status:"error"` when diagrams are unsupported. */
export interface CandidateSection {
  group_id: string;
  section_id: string;
  name: string;
  parent_group?: string | null;
  sheet_count?: number | null;
  count?: number | null;
  current?: boolean;
}

export interface CandidateSectionsResponse {
  status: string;
  query: string;
  sections: CandidateSection[];
}

export interface SectionDiagram {
  status: string;
  group_id: string;
  section_id: string;
  diagram_url: string | null;
  /** The catalog's parent category name (e.g. "BODY"). */
  group_name?: string | null;
  /** The catalog's own section name (e.g. "BACK DOOR & FITTING") — labels the diagram tab. */
  section_name?: string | null;
  callouts: DiagramCallout[];
  /** Every drawing of this section. A section's sheets each carry a different
   *  slice of the parts, so the viewer needs them all. Absent on payloads
   *  cached before multi-sheet shipped — the viewer falls back to one sheet. */
  sheets?: Array<{
    index: number;
    id?: string | null;
    /** Per-sheet identity from the drawing id; group·section is the same on all. */
    code?: string | null;
    /** The drawing's own title, when the vendor has one. */
    title?: string | null;
    diagram_url: string | null;
    callouts: DiagramCallout[];
    part_callouts?: string[];
  }> | null;
  sheet_count?: number | null;
  primary_sheet?: number | null;
  parts: Array<{
    partId: string;
    callout: string | null;
    /** Localized part name/description (already in the client language). */
    partName: string | null;
    /** Real manufacturer/catalog part number, distinct from the internal partId. */
    part_number?: string | null;
    /** Side/hand/position attributes not in the name, as label→value pairs.
     *  Distinguishes same-named diagram parts (P1 vs P2) — shown as chips. */
    extra_details?: Record<string, string> | null;
    /** The backend's reading of the row's own text (side, position, height,
     *  placement, component) in a closed vocabulary — one reading per catalog
     *  string, any language or format. Absent until the row has been read. */
    axes?: { side?: 'left' | 'right' | 'both' | null; position?: 'front' | 'rear' | 'both' | null; height?: 'upper' | 'lower' | null; placement?: 'inner' | 'outer' | null; component?: string | null } | null;
  }>;
  message?: string;
}

export interface StreamEvent {
  type: 'event' | 'final' | 'error' | 'quota_exceeded';
  data: {
    event_type?: string;
    is_final?: boolean;
    /**
     * True when the server will fire another agent iteration after this final
     * response (i.e. a CONTINUATION_ACTION was detected).  The client should
     * render the message immediately but keep the loading / typing indicator
     * active until a subsequent event with has_continuation=false arrives.
     */
    has_continuation?: boolean;
    /**
     * True on a replayed final event returned by the server-side idempotency
     * check.  The client should call onComplete without rendering the message
     * again (the response was already shown from the first successful delivery).
     */
    replayed?: boolean;
    // Support for multiple parts results
    parts_result?: PartResult[];
    parts_list?: Array<any> | null;
    context_tags?: Array<string> | null;
    content?: string;
    annotated_images: string[] | null;
    repair_instructions?: string | null;
    search_summarization?: any | null;
    callToAction?: string | null;
    not_found_parts?: string[] | null;
    requested_parts?: string[] | null;
    ext_search_results?: ExtSearchResults | null;
    timestamp?: string;
    response?: string;
    status: string;
  };
  error_details?: {
    message: string;
    stack?: string;
    name?: string;
    status?: number;
    response_text?: string;
  };
}

/** Where a catax_3d model build is: 0..1 plus the service's stage ("modeling") and its detail ("6/26 body parts"). */
export type Diagram3dModelProgress = { progress: number; stage: string; detail: string | null };

class ChatService {
  // Section diagrams are immutable per (session, group, section); cache the fetch
  // promise so re-mounting/re-rendering a clarification or results view reuses it
  // instead of hitting /section-diagram again. See getSectionDiagram.
  private static _sectionDiagramCache = new Map<string, Promise<SectionDiagram>>();
  private static readonly _SECTION_DIAGRAM_STORAGE_PREFIX = 'catax:section-diagram:v2:';
  private static readonly _SECTION_DIAGRAM_STORAGE_MAX = 20;
  private static readonly _SECTION_DIAGRAM_STORAGE_TTL_MS = 12 * 60 * 60 * 1000;

  // The in-memory cache above disappears on every page refresh. Persist the
  // immutable response in this tab as well so restoring an old clarification
  // does not reopen an expired catalog/device session before it can show the
  // diagram. The image URLs are stable public GCS URLs; sessionStorage keeps the
  // catalog metadata scoped to the current browser tab and clears naturally
  // when that tab is closed.
  private static _sectionDiagramStorageKey(sessionId: string, groupId: string, sectionId: string, partId?: string): string {
    const base = `${ChatService._SECTION_DIAGRAM_STORAGE_PREFIX}${encodeURIComponent(sessionId)}:${encodeURIComponent(groupId)}:${encodeURIComponent(sectionId)}`;
    return partId ? `${base}:${encodeURIComponent(partId)}` : base;
  }

  private static _storedSectionDiagrams(): Array<{ key: string; cachedAt: number; lastAccessed: number }> {
    if (typeof sessionStorage === 'undefined') return [];
    const now = Date.now();
    const entries: Array<{ key: string; cachedAt: number; lastAccessed: number }> = [];
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const key = sessionStorage.key(i);
      if (!key?.startsWith(ChatService._SECTION_DIAGRAM_STORAGE_PREFIX)) continue;
      try {
        const parsed = JSON.parse(sessionStorage.getItem(key) || '{}');
        const cachedAt = Number(parsed.cachedAt);
        const lastAccessed = Number(parsed.lastAccessed ?? cachedAt);
        if (!Number.isFinite(cachedAt) || now - cachedAt > ChatService._SECTION_DIAGRAM_STORAGE_TTL_MS) {
          sessionStorage.removeItem(key);
          continue;
        }
        entries.push({ key, cachedAt, lastAccessed: Number.isFinite(lastAccessed) ? lastAccessed : cachedAt });
      } catch {
        sessionStorage.removeItem(key);
      }
    }
    return entries;
  }

  private static _readStoredSectionDiagram(sessionId: string, groupId: string, sectionId: string, partId?: string): SectionDiagram | null {
    try {
      if (typeof sessionStorage === 'undefined') return null;
      const raw = sessionStorage.getItem(ChatService._sectionDiagramStorageKey(sessionId, groupId, sectionId, partId));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const cachedAt = Number(parsed?.cachedAt);
      const value = parsed?.value as SectionDiagram | undefined;
      if (!Number.isFinite(cachedAt) || Date.now() - cachedAt > ChatService._SECTION_DIAGRAM_STORAGE_TTL_MS) {
        sessionStorage.removeItem(ChatService._sectionDiagramStorageKey(sessionId, groupId, sectionId, partId));
        return null;
      }
      if (value?.status !== 'success' || value.group_id !== groupId || value.section_id !== sectionId) return null;
      try {
        sessionStorage.setItem(
          ChatService._sectionDiagramStorageKey(sessionId, groupId, sectionId, partId),
          JSON.stringify({ value, cachedAt, lastAccessed: Date.now() }),
        );
      } catch { /* Access time is an optimization only. */ }
      return value;
    } catch {
      return null;
    }
  }

  private static _storeSectionDiagram(sessionId: string, groupId: string, sectionId: string, value: SectionDiagram, partId?: string): void {
    if (value.status !== 'success') return;
    try {
      if (typeof sessionStorage !== 'undefined') {
        const key = ChatService._sectionDiagramStorageKey(sessionId, groupId, sectionId, partId);
        const entries = ChatService._storedSectionDiagrams()
          .filter(entry => entry.key !== key)
          .sort((a, b) => a.lastAccessed - b.lastAccessed);
        while (entries.length >= ChatService._SECTION_DIAGRAM_STORAGE_MAX) {
          const oldest = entries.shift();
          if (oldest) sessionStorage.removeItem(oldest.key);
        }
        const payload = JSON.stringify({ value, cachedAt: Date.now(), lastAccessed: Date.now() });
        try {
          sessionStorage.setItem(key, payload);
        } catch {
          // If the browser quota is shared with other session data, free one more
          // of our own least-recently-used entries and retry once.
          const oldest = entries.shift();
          if (!oldest) return;
          sessionStorage.removeItem(oldest.key);
          sessionStorage.setItem(key, payload);
        }
      }
    } catch {
      // Storage can be disabled or full; the existing in-memory cache still works.
    }
  }

  // Guest/portal-mode override: GuestChatPage / PortalChatPage register their base
  // URL + bearer token (and endpoint path) here so shared components
  // (ClarificationDiagramInline via getSectionDiagram) hit the matching scoped
  // endpoint. apiService's Firebase auth doesn't exist on those pages, so without
  // this the diagram fetch 401s and every section shows "Diagram not available".
  // Cleared (null token) when the page unmounts.
  private static _guestAuth: { baseUrl: string; token: string; path: string } | null = null;

  setGuestAuth(baseUrl: string, token: string | null, path = '/api/guest/chat/section-diagram') {
    ChatService._guestAuth = token ? { baseUrl, token, path } : null;
  }

  // Guest/portal pages have no SessionProvider, so useActiveSessionIdSafe()
  // returns '' there and shared components (ClarificationDiagramInline) can't
  // know which session to fetch diagrams for. The guest page registers its
  // active session id here as a fallback.
  private static _guestSessionId: string | null = null;

  setGuestSessionId(sessionId: string | null) {
    ChatService._guestSessionId = sessionId;
  }

  getGuestSessionId(): string {
    return ChatService._guestSessionId ?? '';
  }

  constructor() {
    // Constructor kept for potential future initialization
  }

  // Streaming chat method using Server-Sent Events
  async sendMessageStream(
    message: string,
    sessionId: string,
    language: string,
    onEvent: (event: StreamEvent) => void,
    onComplete: () => void,
    onError: (error: string, errorDetails?: any) => void,
    contextTags?: string[],
    filesData?: Array<{ data: string; mimeType: string; gcsUri?: string }>,
    abortSignal?: AbortSignal,
    onRetry?: (attempt: number, maxAttempts: number, reason: string) => void,
    structuredAnswer?: Record<string, unknown>,
    partSelectionMode?: string
  ): Promise<void> {
    const STALL_TIMEOUT  =  90000;  // 90 s no-data stall: backend heartbeats every 10 s, plenty of margin
    const HEARTBEAT_INTERVAL = 30000; // log-only stall warning interval
    // MAX_RETRY_ATTEMPTS = 4: safe because we use client_message_uuid as an
    // idempotency key — the backend detects duplicate submissions and either
    // re-emits the cached final event or re-attaches to the live stream.
    const MAX_RETRY_ATTEMPTS = 4;
    const RETRY_BASE_DELAY = 1500;
    const RETRY_MAX_DELAY = 12000;

    let retryCount = 0;
    // Persists across retries so the server can skip already-delivered events
    let lastEventId: string | null = null;

    // Stable idempotency key for this send action — generated once and reused on
    // every retry attempt so the backend can detect and deduplicate the request.
    // We use crypto.randomUUID() when available (all modern browsers), with a
    // simple fallback for environments that don't support it.
    const clientMessageUuid: string =
      (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

    // Helper to determine if an error event is retryable
    const isRetryableError = (eventData: any): boolean => {
      // Prefer the server's structured verdict (error frames carry `retryable`
      // + a stable code); the substring list below is only a legacy fallback.
      const structured =
        eventData.error_details?.retryable ??
        eventData.data?.error_details?.retryable ??
        eventData.data?.retryable;
      if (typeof structured === 'boolean') return structured;
      const errorMessage = eventData.data?.content?.toLowerCase() || '';
      const errorType = eventData.data?.event_type?.toLowerCase() || '';

      // List of retryable error patterns
      const retryablePatterns = [
        'timeout',
        'connection',
        'network',
        'temporarily unavailable',
        'service unavailable',
        'gateway timeout',
        'bad gateway',
        'rate limit',
        'too many requests',
        'server error',
        '503',
        '504',
        '502',
        '429',
      ];

      // Check if error message contains any retryable pattern
      return retryablePatterns.some(pattern =>
        errorMessage.includes(pattern) || errorType.includes(pattern)
      );
    };

    // Retry wrapper for handling network disconnections
    const attemptStream = async (): Promise<void> => {
      let stallTimeoutId: NodeJS.Timeout | null = null;
      let heartbeatId: NodeJS.Timeout | null = null;
      let lastDataReceived = Date.now();
      let hasReceivedData = false;
      // Guard: once onError has been called, suppress the subsequent onComplete that
      // fires because reader.cancel() causes the while-loop to exit normally.
      let errorFired = false;
      // Guard: the terminal `final` frame settles the UI immediately (see below);
      // the natural end-of-stream must not call onComplete a second time.
      let completeFired = false;
      const settle = () => {
        if (errorFired || completeFired) return;
        completeFired = true;
        onComplete();
      };
      let readerRef: ReadableStreamDefaultReader<Uint8Array> | null = null;

      // ── Visibility-aware stall timer ────────────────────────────────────────
      // When the tab is hidden (user switches tabs, minimises, or the OS suspends
      // the app) the browser throttles/freezes JS timers.  The stall timeout would
      // fire the instant the tab becomes visible again — incorrectly marking a
      // perfectly healthy stream as failed.
      // Solution: pause the stall timer while the document is hidden and resume it
      // (starting fresh) when it becomes visible again.
      let stallRemainingMs = 0; // remaining ms when the timer was paused
      let stallPausedAt: number | null = null; // timestamp when we paused

      const pauseStallTimer = () => {
        if (stallTimeoutId !== null && stallPausedAt === null) {
          clearTimeout(stallTimeoutId);
          stallTimeoutId = null;
          stallPausedAt = Date.now();
          stallRemainingMs = Math.max(0, STALL_TIMEOUT - (stallPausedAt - lastDataReceived));
          console.debug('[stall] Stall timer paused — page hidden');
        }
      };

      const resumeStallTimer = () => {
        if (stallPausedAt !== null) {
          stallPausedAt = null;
          // Reset lastDataReceived so the resumed timer counts from now, and
          // always give a fresh full timeout — the backend keeps sending heartbeats
          // while the tab is blurred, so the stream is still alive.
          lastDataReceived = Date.now();
          stallRemainingMs = STALL_TIMEOUT;
          console.debug('[stall] Stall timer resumed — page visible, restarting full timeout');
          scheduleStallTimeout(STALL_TIMEOUT);
        }
      };

      const handleVisibilityForStall = () => {
        if (document.visibilityState === 'hidden') {
          pauseStallTimer();
        } else {
          resumeStallTimer();
        }
      };

      // On desktop, switching to another window keeps visibilityState = 'visible'
      // but the browser may still throttle timers.  Pause on blur too.
      const handleBlurForStall = () => pauseStallTimer();
      const handleFocusForStall = () => resumeStallTimer();

      document.addEventListener('visibilitychange', handleVisibilityForStall);
      window.addEventListener('blur', handleBlurForStall);
      window.addEventListener('focus', handleFocusForStall);

      const cleanup = () => {
        if (stallTimeoutId) clearTimeout(stallTimeoutId);
        if (heartbeatId) clearInterval(heartbeatId);
        document.removeEventListener('visibilitychange', handleVisibilityForStall);
        window.removeEventListener('blur', handleBlurForStall);
        window.removeEventListener('focus', handleFocusForStall);
      };

      // Core stall-timeout scheduler (separated so it can be called with a custom delay).
      const scheduleStallTimeout = (delayMs: number) => {
        if (stallTimeoutId) clearTimeout(stallTimeoutId);
        stallTimeoutId = setTimeout(() => {
          console.warn('Stream stalled — no data received for', STALL_TIMEOUT, 'ms; cancelling reader');

          // If the abort signal is already set (e.g. user hid the app / navigated away
          // and the visibilitychange handler fired), treat this as a user-initiated
          // cancellation — not an error — so no "No response received" banner appears.
          if (abortSignal?.aborted) {
            console.log('Stream stalled but abort was already requested — suppressing error');
            readerRef?.cancel();
            cleanup();
            return;
          }

          // The answer already arrived (terminal `final` frame). Only the
          // best-effort trailing frames (complementary suggestions) are
          // outstanding — a stall now is not an error the user should see,
          // and certainly not a retry of a turn that completed.
          if (completeFired) {
            console.log('Stream stalled after the final frame — closing quietly');
            readerRef?.cancel();
            cleanup();
            return;
          }

          readerRef?.cancel();
          if (!errorFired) {
            // If we still have retries left, let the catch block retry rather than
            // surfacing the error to the user immediately.
            if (retryCount < MAX_RETRY_ATTEMPTS) {
              console.log(`Stall timeout — retrying (${retryCount + 1}/${MAX_RETRY_ATTEMPTS})`);
              cleanup();
              readerRef = null; // prevent double-cancel
              retryCount++;
              if (onRetry) {
                onRetry(retryCount, MAX_RETRY_ATTEMPTS, 'Connection stalled');
              }
              const delay = Math.min(RETRY_BASE_DELAY * Math.pow(2, retryCount - 1), RETRY_MAX_DELAY);
              setTimeout(() => attemptStream(), delay);
            } else {
              errorFired = true;
              cleanup();
              onError('The connection stalled. Please check your internet connection and try again.', {
                name: 'StreamStall',
                message: `No data received for ${STALL_TIMEOUT}ms`,
              });
            }
          }
        }, delayMs);
      };

      const resetStallTimeout = () => {
        stallPausedAt = null;
        lastDataReceived = Date.now();
        scheduleStallTimeout(STALL_TIMEOUT);
      };

      // Reset the stall timer on any received SSE activity — including heartbeat
      // comment lines (": heartbeat") which carry no data payload.
      const onActivity = () => {
        lastDataReceived = Date.now();
        // Only reschedule the timer if the page is visible AND the timer is not
        // currently paused (blur/hide).  If paused, skip — resumeStallTimer will
        // start a fresh full-length timeout when focus returns.
        if (document.visibilityState !== 'hidden' && stallPausedAt === null) {
          scheduleStallTimeout(STALL_TIMEOUT);
        }
      };

      // Pre-flight check: verify network connectivity before attempting
      if (retryCount === 0 && !navigator.onLine) {
        const error = new Error('No internet connection detected. Please check your network.');
        const errorDetails = {
          message: error.message,
          stack: error.stack,
          name: 'NetworkOffline',
          retryCount: 0,
        };
        onError('You appear to be offline. Please check your internet connection.', errorDetails);
        return;
      }

      try {
        // Save stream state for potential reconnection — include clientMessageUuid
        // so that if the page is refreshed mid-retry the UUID is preserved.
        this.saveStreamState(sessionId, lastEventId, clientMessageUuid);
        const requestBody: any = {
          // Prefix with SYSTEM_RETRY: on retry attempts so the backend (and UI)
          // can identify this as an automatic retry and suppress display.
          message: retryCount > 0 ? `SYSTEM_RETRY: ${message}` : message,
          language,
          session_id: sessionId,
          // Idempotency key: stable across retries so the backend can detect
          // a duplicate submission and avoid running the agent twice.
          client_message_uuid: clientMessageUuid,
        };

        if (contextTags && contextTags.length > 0) {
          requestBody.context_tags = contextTags;
        }

        // Structured clarification answer — the backend ChatMessage accepts an
        // optional `structured_answer` field alongside `message`.
        if (structuredAnswer) {
          requestBody.structured_answer = structuredAnswer;
        }

        // Admin-only part-selection mode override. Only attached for admins (the
        // caller passes undefined otherwise), so regular-user requests are
        // byte-for-byte unchanged. The backend ignores any unrecognised value.
        if (partSelectionMode) {
          requestBody.part_selection_mode = partSelectionMode;
        }

        // Add files data if provided (supports multiple files)
        if (filesData && filesData.length > 0) {
          requestBody.uploadedFiles = filesData;
        }

        // SSE resume: tell the server which event we last received so it can
        // skip already-delivered events and avoid duplicate messages on reconnect.
        const resumeHeaders: Record<string, string> = {};
        if (lastEventId) {
          requestBody.last_event_id = lastEventId;
          resumeHeaders['Last-Event-ID'] = lastEventId;
        }

        const response = await apiService.authenticatedFetch('/api/chat/endpoint', {
          method: 'POST',
          body: JSON.stringify(requestBody),
          signal: abortSignal,
          headers: resumeHeaders,
        });

        if (!response.ok) {
          let errorMessage = `HTTP error! status: ${response.status}`;
          let responseText = '';

          try {
            responseText = await response.text();
          } catch (e) {
            // Ignore text reading errors
          }

          if (response.status === 401) {
            errorMessage = 'Authentication required. Please sign in again.';
          } else if (response.status === 429) {
            errorMessage = 'Rate limit exceeded. Retrying...';
            // Throw specific error for retry with Retry-After header info
            const retryAfter = response.headers.get('Retry-After');
            const error: any = new Error('RATE_LIMIT_EXCEEDED');
            if (retryAfter) {
              error.retryAfter = retryAfter;
            }
            throw error;
          } else if (response.status === 499) {
            // 499 means the CLIENT disconnected from the server — the user navigated
            // away or the page was unloaded. Re-sending the same message would create
            // a duplicate on the backend, so we treat this as a user-initiated abort
            // rather than a retryable error.
            console.log('Client disconnected (499) — treating as user abort, not retrying');
            this.clearStreamState();
            return; // Silently exit without calling onError or retrying
          } else if (response.status >= 500) {
            errorMessage = 'Server error. Retrying...';
            throw new Error('SERVER_ERROR');
          }

          const error = new Error(errorMessage);
          const errorDetails = {
            message: errorMessage,
            stack: error.stack,
            name: 'HTTPError',
            status: response.status,
            response_text: responseText,
          };

          cleanup();
          throw { ...error, details: errorDetails };
        }

        const reader = response.body?.getReader();
        if (!reader) {
          cleanup();
          throw new Error('Unable to read response stream');
        }
        readerRef = reader;

        // Stall timer — fires when no data arrives within STALL_TIMEOUT
        resetStallTimeout();

        // Heartbeat interval — log-only warning for long-running streams
        heartbeatId = setInterval(() => {
          const timeSinceLastData = Date.now() - lastDataReceived;
          if (timeSinceLastData > HEARTBEAT_INTERVAL * 2) {
            console.warn('No data received for', timeSinceLastData, 'ms — connection may be stalled');
          }
        }, HEARTBEAT_INTERVAL);

        const decoder = new TextDecoder();
        let buffer = '';

        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              break;
            }

            hasReceivedData = true;

            // Reset stall timer on every chunk
            onActivity();

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n\n');

            // Process complete messages
            for (let i = 0; i < lines.length - 1; i++) {
              const messageParts = lines[i].split('\n');
              let eventId: string | null = null;
              let eventDataLine: string | null = null;

              for (const part of messageParts) {
                if (part.startsWith('id: ')) {
                  eventId = part.substring(4).trim();
                } else if (part.startsWith('data: ')) {
                  eventDataLine = part;
                } else if (part.startsWith(': ')) {
                  // Heartbeat comment — reset the stall timer so we don't
                  // time out on long-running but alive streams.
                  onActivity();
                  console.debug('Received heartbeat from server');
                }
              }

              if (eventDataLine) {
                try {
                  const eventData = JSON.parse(eventDataLine.substring(6));

                  // Track last event ID for resume capability
                  if (eventId) {
                    lastEventId = eventId;
                    this.saveStreamState(sessionId, lastEventId);
                  }

                  // Skip replayed final events from the idempotency path — the server
                  // re-emits them when a retry detects a completed duplicate.  We still
                  // call onComplete so the UI settles correctly.
                  if ((eventData as StreamEvent).data?.replayed === true) {
                    cleanup();
                    this.clearStreamState();
                    if (!errorFired) {
                      onComplete();
                    }
                    return;
                  }

                  // ── Plan quota terminal frame ────────────────────────────────
                  // The backend ends the turn with an in-band quota_exceeded
                  // frame (never a transport error, so no retry). Publish it
                  // globally (banner + composer disable), let the hook render
                  // the in-conversation notice, and settle the stream.
                  if ((eventData as StreamEvent).type === 'quota_exceeded') {
                    try {
                      const { quotaBus } = await import('./quotaBus');
                      quotaBus.publish((eventData as any).data);
                    } catch { /* banner is best-effort */ }
                    onEvent(eventData as StreamEvent);
                    cleanup();
                    this.clearStreamState();
                    if (!errorFired) {
                      onComplete();
                    }
                    return;
                  }

                  // ── Reflect-and-retry plugin error leak guard ─────────────────
                  // If all ADK retries are exhausted the plugin emits a raw JSON blob with
                  // response_type=ERROR_HANDLED_BY_REFLECT_AND_RETRY_PLUGIN.  Never show
                  // this internal payload to the user — treat it as a server error so the
                  // retry button appears instead.
                  const _content = (eventData as StreamEvent).data?.content;
                  if (typeof _content === 'string' && _content.trimStart().startsWith('{')) {
                    try {
                      const _parsed = JSON.parse(_content.trim());
                      if (_parsed?.response_type === 'ERROR_HANDLED_BY_REFLECT_AND_RETRY_PLUGIN') {
                        // Swallow this event entirely — if it was the final one, fire onError.
                        if ((eventData as StreamEvent).data?.is_final) {
                          cleanup();
                          if (!errorFired) {
                            errorFired = true;
                            onError('server_error');
                          }
                        }
                        continue; // skip onEvent
                      }
                    } catch { /* not JSON — fall through */ }
                  }

                  onEvent(eventData as StreamEvent);

                  // ── Terminal frame: settle NOW, keep reading ─────────────────
                  // The answer (cards + summary) is complete once the backend
                  // sends `type: "final"`. Anything after it on the wire is a
                  // best-effort enhancement (`complementary_update` suggestions,
                  // bookkeeping) that must not keep the composer disabled or the
                  // typing indicator spinning. Settle the UI here and let the
                  // loop drain the trailing frames.
                  if ((eventData as StreamEvent).type === 'final') {
                    this.clearStreamState();
                    settle();
                  }

                  // Handle error events with automatic retry
                  if (eventData.type === 'error') {
                    const shouldRetryError = (
                      retryCount < MAX_RETRY_ATTEMPTS &&
                      !abortSignal?.aborted &&
                      isRetryableError(eventData)
                    );

                    if (shouldRetryError) {
                      // This is a retryable error - retry without interrupting user
                      cleanup();
                      reader.releaseLock();

                      retryCount++;
                      console.log(`Retryable error event received. Retrying (${retryCount}/${MAX_RETRY_ATTEMPTS})...`);

                      // Calculate exponential backoff with jitter
                      const exponentialDelay = Math.min(
                        RETRY_BASE_DELAY * Math.pow(2, retryCount - 1),
                        RETRY_MAX_DELAY
                      );
                      const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
                      const delay = Math.max(RETRY_BASE_DELAY, exponentialDelay + jitter);

                      // Notify UI about retry attempt if callback provided
                      if (onRetry) {
                        const retryReason = eventData.data?.content || 'Temporary error';
                        onRetry(retryCount, MAX_RETRY_ATTEMPTS, retryReason);
                      }

                      // Wait before retrying
                      await new Promise(resolve => setTimeout(resolve, delay));

                      // Retry the stream
                      return attemptStream();
                    } else {
                      // Non-retryable error or max retries exceeded
                      const errorDetails = {
                        message: eventData.data.content || 'Stream processing error',
                        stack: new Error().stack,
                        name: 'StreamError',
                        event_data: eventData.data,
                        retryCount,
                        maxRetries: MAX_RETRY_ATTEMPTS,
                      };

                      cleanup();

                      let userMessage = errorDetails.message;
                      if (retryCount > 0) {
                        userMessage = `Failed after ${retryCount} retry attempt${retryCount > 1 ? 's' : ''}. ${userMessage}`;
                      }

                      errorFired = true;
                      onError(
                        userMessage,
                        { ...errorDetails, ...eventData.error_details }
                      );
                      return;
                    }
                  }
                } catch (parseError) {
                  console.error('Failed to parse event data:', parseError);
                }
              }
            }

            // Keep the last incomplete line in the buffer
            buffer = lines[lines.length - 1];
          }
        } finally {
          reader.releaseLock();
          cleanup();
        }

        cleanup();
        this.clearStreamState();
        settle();
      } catch (error) {
        cleanup();

        // Handle abort
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('Stream aborted by user');
          this.clearStreamState();
          return;
        }

        // Check if we should retry
        const shouldRetry = (
          retryCount < MAX_RETRY_ATTEMPTS &&
          !abortSignal?.aborted &&
          (
            error instanceof Error && (
              error.message === 'SERVER_ERROR' ||
              error.message === 'RATE_LIMIT_EXCEEDED' ||
              error.message.includes('Failed to fetch') ||
              error.message.includes('NetworkError') ||
              error.message.includes('network')
            )
          )
        );

        if (shouldRetry) {
          retryCount++;
          console.log(`Connection lost. Retrying (${retryCount}/${MAX_RETRY_ATTEMPTS})...`);

          // Calculate exponential backoff with jitter
          let exponentialDelay = Math.min(
            RETRY_BASE_DELAY * Math.pow(2, retryCount - 1),
            RETRY_MAX_DELAY
          );

          // For rate limit errors, check if we have Retry-After header
          if (error instanceof Error && error.message === 'RATE_LIMIT_EXCEEDED') {
            const retryAfter = (error as any).retryAfter;
            if (retryAfter) {
              // Retry-After can be in seconds or a date
              const retryAfterMs = !isNaN(retryAfter)
                ? parseInt(retryAfter) * 1000
                : new Date(retryAfter).getTime() - Date.now();
              exponentialDelay = Math.min(Math.max(retryAfterMs, exponentialDelay), RETRY_MAX_DELAY);
            } else {
              // For 429 without Retry-After, use longer backoff
              exponentialDelay = Math.min(exponentialDelay * 2, RETRY_MAX_DELAY);
            }
          }

          // Add random jitter (±25%) to prevent thundering herd
          const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
          const delay = Math.max(RETRY_BASE_DELAY, exponentialDelay + jitter);

          // Determine user-friendly retry reason
          let retryReason = 'Connection issue';
          if (error instanceof Error) {
            if (error.message === 'SERVER_ERROR') {
              retryReason = 'Server error';
            } else if (error.message === 'RATE_LIMIT_EXCEEDED') {
              retryReason = 'Rate limit exceeded';
            } else if (error.message.includes('network')) {
              retryReason = 'Network error';
            }
          }

          // Notify UI about retry attempt if callback provided
          if (onRetry) {
            onRetry(retryCount, MAX_RETRY_ATTEMPTS, retryReason);
          }

          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, delay));

          // Retry the stream
          return attemptStream();
        }

        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        let errorDetails = null;

        if (error instanceof Error) {
          errorDetails = {
            message: error.message,
            stack: error.stack,
            name: error.name,
            retryCount,
            maxRetries: MAX_RETRY_ATTEMPTS,
            isRetryable: shouldRetry || retryCount > 0,
            ...(error as any).details, // Include any additional details we attached
          };
        } else if ((error as any)?.details) {
          errorDetails = (error as any).details;
        }

        // Provide user-friendly error messages
        let userFriendlyMessage = errorMessage;
        if (retryCount > 0) {
          userFriendlyMessage = `Connection failed after ${retryCount} retry attempt${retryCount > 1 ? 's' : ''}. Please check your internet connection and try again.`;
        } else if (errorMessage.includes('Authentication required')) {
          userFriendlyMessage = 'Your session has expired. Please refresh the page and sign in again.';
        } else if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
          userFriendlyMessage = 'Unable to connect to the server. Please check your internet connection.';
        } else if (errorMessage.includes('timeout')) {
          userFriendlyMessage = 'The request is taking longer than expected. Please try again or use a shorter message.';
        }

        this.clearStreamState();
        onError(userFriendlyMessage, errorDetails);
      }
    };

    // Start the streaming attempt
    return attemptStream();
  }

  // Legacy method for non-streaming requests (kept for backward compatibility)
  async sendMessage(message: string, sessionId: string): Promise<ChatResponse> {
    try {
      const response = await apiService.post<ChatResponse>('/api/chat', {
        message,
        session_id: sessionId,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error('Authentication required. Please sign in again.');
        }
        if (error.response) {
          // Server responded with error status
          throw new Error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          // Request was made but no response received
          throw new Error('Unable to connect to the server. Please check your connection.');
        }
      }

      // Generic error fallback
      throw new Error('An unexpected error occurred. Please try again.');
    }
  }

  // Method to test connection to the API
  async testConnection(): Promise<boolean> {
    try {
      await apiService.get('/api/health');
      return true;
    } catch {
      return false;
    }
  }

  // Method to get API base URL (useful for debugging)
  getBaseURL(): string {
    return process.env.REACT_APP_API_URL || '';
  }

  // Method to get part image URL
  async getPartImage(vin: string, datasetId: string, imageId: string): Promise<string> {
    try {
      const response = await apiService.get<{ image_url: string }>(`/api/image/${vin}/${datasetId}/${imageId}`);
      return response.data.image_url;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        throw new Error('Authentication required. Please sign in again.');
      }
      throw new Error('Failed to fetch part image.');
    }
  }

  // Fetch a catalog section's exploded-view diagram for a clarification question.
  // Returns the diagram image URL, the callout hotspots, and the parts in the
  // section (partId → callout). The UI highlights the relevant parts on the diagram.
  //
  // A section's diagram is immutable for a given (session, group, section), yet the
  // clarification/results views re-mount and re-render freely — so without a cache
  // the SAME diagram was re-fetched on every open. We memoize the in-flight promise
  // (keyed by session/group/section), which both dedupes concurrent callers and
  // serves instant repeats; a rejected fetch is evicted so a later open can retry.
  // (This also spares the microcat device session redundant section/image legs that
  // 403 once it goes stale.)
  /** POST /api/chat/clarification-hint — an AI note that helps the user choose
   *  between a clarification question's candidates. Signed-in users only (the
   *  guest/portal auth override has no hint endpoint). */
  async getClarificationHint(payload: ClarificationHintRequest): Promise<ClarificationHint> {
    const response = await apiService.post<ClarificationHint>('/api/chat/clarification-hint', payload);
    return response.data;
  }

  /** POST /api/chat/clarification-chat — the assistant's next turn in the chat
   *  about one clarification question (signed-in users only). */
  async clarificationChat(payload: ClarificationChatRequest): Promise<ClarificationChatReply> {
    const response = await apiService.post<ClarificationChatReply>('/api/chat/clarification-chat', payload);
    return response.data;
  }

  /** GET /api/chat/clarification-chat/history — the Part Picker conversation
   *  for a pending question (empty when there is none), so the sidebar picks
   *  up where it left off after a reload. */
  async clarificationChatHistory(sessionId: string, questionId: string): Promise<ClarificationChatHistoryMessage[]> {
    const response = await apiService.get<{ messages: ClarificationChatHistoryMessage[] }>(
      '/api/chat/clarification-chat/history',
      { params: { session_id: sessionId, question_id: questionId } },
    );
    return response.data?.messages ?? [];
  }

  /** AI-generated 3D rendering of a diagram drawing (the viewer's
   *  "3D · AI generated" view). The backend renders each drawing once and
   *  serves it from storage after that; here a successful answer is also kept
   *  for the page's lifetime. No automatic retry: a render costs a model call,
   *  and a cancelled one must not be fired again behind the user's back. */
  /** The viewer's "Model" view: start (or reuse) the catax_3d build of this
   *  drawing with its part numbers, poll until it is done, and resolve to the
   *  viewer URL to show in an iframe. The backend remembers the job per
   *  drawing + part list, so a reopen resumes rather than rebuilds. */
  async buildDiagram3dModel(url: string, parts: Array<{ pn: string; name: string | null; alt: string[] }>, signal?: AbortSignal, rebuild = false,
    onProgress?: (p: Diagram3dModelProgress) => void): Promise<string> {
    type Job = { id: string; status: string; progress?: number | null; stage?: string | null; stage_detail?: string | null; viewer?: string | null; error?: string | null; issues?: Array<{ code: string; message?: string }> };
    let job = (await apiService.post<Job>('/api/chat/diagram-3d-models', { url, parts, rebuild }, { signal })).data;
    while (job.status !== 'done' && job.status !== 'failed') {
      onProgress?.({ progress: job.progress ?? 0, stage: job.stage ?? job.status, detail: job.stage_detail ?? null });
      await new Promise<void>((res, rej) => {
        const t = setTimeout(res, 3000);
        signal?.addEventListener('abort', () => { clearTimeout(t); rej(new DOMException('aborted', 'AbortError')); }, { once: true });
      });
      job = (await apiService.get<Job>(`/api/chat/diagram-3d-models/${job.id}`, { signal })).data;
    }
    if (job.status !== 'done' || !job.viewer) throw new Error(job.error || 'diagram-3d-models job failed');
    // The service still "finishes" when the 3D generator was unavailable, with flat
    // placeholder shapes for every body part. That is not a model: fail visibly.
    const noGenerator = job.issues?.find(i => i.code === 'backend_unavailable');
    if (noGenerator) throw new Error(noGenerator.message || 'diagram-3d-models: 3D generator unavailable');
    return job.viewer;
  }

  private static _diagram3dCache = new Map<string, string>();
  async getDiagram3d(url: string, sessionId?: string | null, signal?: AbortSignal): Promise<string> {
    const hit = ChatService._diagram3dCache.get(url);
    if (hit) return hit;
    const body = JSON.stringify({ url, session_id: sessionId || null });
    const ga = ChatService._guestAuth;
    const res = ga
      ? await fetch(`${ga.baseUrl}${ga.path.replace(/section-diagram$/, 'diagram-3d')}`, {
        method: 'POST', body, signal,
        headers: { Authorization: `Bearer ${ga.token}`, 'Content-Type': 'application/json' },
      })
      : await apiService.authenticatedFetch('/api/chat/diagram-3d', {
        method: 'POST', body, signal, headers: { 'Content-Type': 'application/json' },
      });
    if (!res.ok) throw new Error(`diagram-3d failed: ${res.status}`);
    const data = await res.json() as { status?: string; url?: string };
    if (!data?.url) throw new Error('diagram-3d returned no image');
    ChatService._diagram3dCache.set(url, data.url);
    return data.url;
  }

  /** `partId` picks the illustration page that part is drawn on (multi-page
   *  catalog sections); without it the catalog draws the section's first page. */
  async getSectionDiagram(sessionId: string, groupId: string, sectionId: string, partId?: string): Promise<SectionDiagram> {
    const cacheKey = `${sessionId} ${groupId} ${sectionId} ${partId || ''}`;
    const cached = ChatService._sectionDiagramCache.get(cacheKey);
    if (cached) return cached;
    const stored = ChatService._readStoredSectionDiagram(sessionId, groupId, sectionId, partId);
    if (stored) {
      const restored = Promise.resolve(stored);
      ChatService._sectionDiagramCache.set(cacheKey, restored);
      return restored;
    }
    const ga = ChatService._guestAuth;
    const fetchDiagram = (): Promise<SectionDiagram> => {
      if (ga) {
        const qs = new URLSearchParams({ session_id: sessionId, group_id: groupId, section_id: sectionId, ...(partId ? { part_id: partId } : {}) });
        return fetch(`${ga.baseUrl}${ga.path}?${qs}`, {
          headers: { Authorization: `Bearer ${ga.token}` },
        }).then((res) => {
          if (!res.ok) throw new Error(`section-diagram failed: ${res.status}`);
          return res.json() as Promise<SectionDiagram>;
        });
      }
      return apiService
        .get<SectionDiagram>('/api/chat/section-diagram', {
          params: { session_id: sessionId, group_id: groupId, section_id: sectionId, ...(partId ? { part_id: partId } : {}) },
        })
        .then((response) => response.data);
    };
    const promise = fetchDiagram()
      .then((diagram) => {
        ChatService._storeSectionDiagram(sessionId, groupId, sectionId, diagram, partId);
        return diagram;
      })
      .catch((err) => {
        ChatService._sectionDiagramCache.delete(cacheKey); // allow a retry on next open
        throw err;
      });
    ChatService._sectionDiagramCache.set(cacheKey, promise);
    return promise;
  }

  /** GET /api/chat/candidate-sections — other sections that may hold the part.
   *
   *  Backs the diagram's "other sections" menu, so it is called only when that
   *  menu is opened. Advisory: any failure resolves to an empty list rather
   *  than rejecting, because the diagram the user is already looking at must
   *  not break over a menu. The guest/portal surfaces have no mirror of this
   *  route, so they resolve empty too and simply show no menu. */
  async getCandidateSections(
    sessionId: string, query: string, groupId: string, sectionId: string, limit = 8,
  ): Promise<CandidateSectionsResponse> {
    if (ChatService._guestAuth) return { status: 'success', query, sections: [] };
    try {
      const response = await apiService.get<CandidateSectionsResponse>('/api/chat/candidate-sections', {
        params: { session_id: sessionId, query, group_id: groupId, section_id: sectionId, limit },
      });
      return response.data ?? { status: 'success', query, sections: [] };
    } catch {
      return { status: 'success', query, sections: [] };
    }
  }

  // Method to get user sessions
  async getUserSessions(): Promise<UserSessionsResponse> {
    try {
      const response = await apiService.get<UserSessionsResponse>('/api/chat/user_sessions');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error('Authentication required. Please sign in again.');
        }
        if (error.response) {
          throw new Error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          throw new Error('Unable to connect to the server. Please check your connection.');
        }
      }

      throw new Error('Failed to fetch user sessions.');
    }
  }

  async createSession(language: string): Promise<{ success: boolean; sessionId?: string; error?: string }> {
    try {
      const response = await apiService.post<{ session_id: string }>('/api/chat/create_session', { language });
      return { success: true, sessionId: response.data.session_id };
    } catch (error) {
      let errorMessage = 'Failed to create session.';
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          errorMessage = 'Authentication required. Please sign in again.';
        } else if (error.response) {
          errorMessage = `Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`;
        } else if (error.request) {
          errorMessage = 'Unable to connect to the server. Please check your connection.';
        }
      }
      return { success: false, error: errorMessage };
    }
  }

  // Method to get session details by session ID
  /** Delete a chat session the user owns (Orders list → session rows). */
  async deleteSession(sessionId: string): Promise<any> {
    const response = await apiService.delete(`/api/chat/session/${sessionId}`);
    return response.data;
  }

  async getSessionDetails(sessionId: string): Promise<any> {
    try {
      const response = await apiService.get(`/api/chat/session/${sessionId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error('Authentication required. Please sign in again.');
        }
        if (error.response?.status === 404) {
          throw new Error('Session not found.');
        }
        if (error.response) {
          throw new Error(`Server error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`);
        } else if (error.request) {
          throw new Error('Unable to connect to the server. Please check your connection.');
        }
      }

      throw new Error('Failed to fetch session details.');
    }
  }

  // Method to check streaming status (for reconnection)
  async getStreamingStatus(sessionId: string): Promise<{
    status: string;
    is_streaming: boolean;
    is_interrupted: boolean;
    can_resume: boolean;
    pending_next: boolean;
    pending_next_processing: boolean;
    pending_next_failed: boolean;
    streaming_state?: any;
  }> {
    try {
      const response = await apiService.get(`/api/chat/session/${sessionId}/streaming_status`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error('Authentication required. Please sign in again.');
        }
        if (error.response?.status === 404) {
          throw new Error('Session not found.');
        }
      }
      throw new Error('Failed to fetch streaming status.');
    }
  }

  // Store active stream info in localStorage for reconnection
  saveStreamState(sessionId: string, lastEventId: string | null, clientMessageUuid?: string) {
    const existing = this.getSavedStreamState();
    const streamState = {
      sessionId,
      lastEventId,
      // Preserve the clientMessageUuid from the initial save so retries reuse it
      clientMessageUuid: clientMessageUuid ?? existing?.clientMessageUuid ?? null,
      timestamp: Date.now(),
    };
    localStorage.setItem('active_stream', JSON.stringify(streamState));
  }

  // Get saved stream state for reconnection
  getSavedStreamState(): { sessionId: string; lastEventId: string | null; clientMessageUuid: string | null; timestamp: number } | null {
    try {
      const saved = localStorage.getItem('active_stream');
      if (!saved) return null;

      const state = JSON.parse(saved);
      // Ignore if older than 10 minutes
      if (Date.now() - state.timestamp > 600000) {
        localStorage.removeItem('active_stream');
        return null;
      }
      return state;
    } catch {
      return null;
    }
  }

  // Clear saved stream state
  clearStreamState() {
    localStorage.removeItem('active_stream');
  }

  /**
   * Vector-search the org's embedded data sources for one part term
   * (POST /api/chat/datasource-search). Used by the results table to fetch
   * suggestions for not-found rows whose message payload carries none.
   * `vehicle` ("MG ZS") enriches the embedded query so same-brand price-list
   * rows rank first. `enabled: false` means the feature gate is off — callers
   * should stop asking.
   */
  async searchDataSources(query: string, vehicle?: string): Promise<{ enabled: boolean; vehicle?: string; suggestions: any[] }> {
    const response = await apiService.post('/api/chat/datasource-search', {
      query,
      ...(vehicle ? { vehicle } : {}),
    });
    return response.data;
  }

  /**
   * Pre-upload a chat attachment to GCS immediately after the user selects the
   * file (before the message is sent).  Returns the GCS URI and MIME type so
   * the file reference can be stored instead of the raw base64 bytes.
   *
   * Throws on network / server errors so callers can fall back gracefully.
   */
  async uploadChatFile(
    base64Data: string,
    mimeType: string,
    fileName: string,
    sessionId: string,
  ): Promise<{ gcsUri: string; mimeType: string }> {
    // Convert base64 → Blob
    const byteChars = atob(base64Data);
    const byteNums = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNums[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNums)], { type: mimeType });

    const formData = new FormData();
    formData.append('file', blob, fileName);
    formData.append('session_id', sessionId);

    // Get auth headers but strip Content-Type so the browser can set the
    // correct multipart/form-data boundary automatically.
    const authHeaders = await apiService.getAuthHeaders();
    delete (authHeaders as any)['Content-Type'];

    const fullUrl = `/api/chat/upload-file`;
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const response = await fetch(`${baseUrl}${fullUrl}`, {
      method: 'POST',
      body: formData,
      headers: authHeaders,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Upload failed (${response.status}): ${text}`);
    }

    return response.json();
  }
}

// Export a singleton instance
const chatService = new ChatService();
export default chatService;
